import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

import {
    Modal,
    Paper,
    Slide,
    Theme,
    Typography,
    IconButton,
    useTheme,
    CircularProgress,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    DirectionsWalk as WalkIcon,
    Search as SearchIcon,
} from '@material-ui/icons'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

import { BaseWorkorder, WorkorderStatus, WorkSpaceUser } from '../../../models'
import { insertOrUpdateIdentifiable } from '../../../hooks/services'
import { WorkorderResponse } from '../../../store'
import { Container, MessageForm } from '../../../components'
import { WorkorderRow } from '../../../components/WorkorderLib/WorkorderRow/WorkorderRow'
import { useWorkorderApi } from '../../../contexts'
import { useCompletionVideoPlayback } from '../../../hooks'
import GoBackModal from './GoBackModal'
import VideoPlaybackModal from './VideoPlaybackModal'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    open: boolean
    workorderList: WorkorderResponse[] | null
    workspaceUser?: WorkSpaceUser
    onClose: () => void
    transitionWorkorder: (
        workorder: BaseWorkorder,
        status: WorkorderStatus,
    ) => Promise<WorkorderResponse>
    createGoBack: (
        workorderId: number,
        request: FormData,
    ) => Promise<WorkorderResponse>
    setWorkorderList: Dispatch<SetStateAction<WorkorderResponse[] | null>>
}

export const WalklistModal = (props: Props) => {
    const {
        open,
        workorderList,
        onClose,
        transitionWorkorder,
        createGoBack,
        setWorkorderList,
    } = props

    const theme = useTheme()

    const [openMessageChannel, setOpenMessageChannel] = useState<{
        channel: number
        channelType: 'workorder' | 'unit_notes'
    }>({ channel: -1, channelType: 'workorder' })

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    elevation={24}
                    style={{
                        width: '90vw',
                        maxWidth: '1200px',
                        height: '85vh',
                        maxHeight: '800px',
                        borderRadius: '12px',
                        overflow: 'hidden',
                    }}
                >
                    <WalkList
                        {...props}
                        openMessageChannel={openMessageChannel}
                        setOpenMessageChannel={setOpenMessageChannel}
                        theme={theme}
                        workorderList={workorderList ?? []}
                        loading={workorderList === null}
                        transitionWorkorder={transitionWorkorder}
                        setWorkorderList={setWorkorderList}
                        createGoBack={createGoBack}
                    />
                </Paper>
            </Slide>
        </Modal>
    )
}

interface WalkListProps extends Props {
    openMessageChannel: {
        channel: number
        channelType: 'workorder' | 'unit_notes'
    }
    theme: Theme
    workorderList: WorkorderResponse[]
    loading: boolean
    transitionWorkorder: (
        workorder: BaseWorkorder,
        status: WorkorderStatus,
    ) => Promise<WorkorderResponse>
    setOpenMessageChannel: React.Dispatch<
        React.SetStateAction<{
            channel: number
            channelType: 'workorder' | 'unit_notes'
        }>
    >
    setWorkorderList: Dispatch<SetStateAction<WorkorderResponse[] | null>>
    createGoBack: (
        workorderId: number,
        request: FormData,
    ) => Promise<WorkorderResponse>
}

export const WalkList = (props: WalkListProps) => {
    const {
        workorderList,
        workspaceUser,
        openMessageChannel,
        theme,
        setOpenMessageChannel,
        transitionWorkorder,
        onClose,
        setWorkorderList,
        loading,
        createGoBack,
    } = props

    const materialTheme = useTheme()

    const [searchTerm, setSearchTerm] = useState('')

    const [openGoBackModal, setOpenGoBackModal] = useState<{
        open: boolean
        workorder: WorkorderResponse | null
    }>({
        open: false,
        workorder: null,
    })

    const [videoModal, setVideoModal] = useState<{
        open: boolean
        workorder: WorkorderResponse | null
    }>({
        open: false,
        workorder: null,
    })

    const {
        url: videoUrl,
        loading: videoLoading,
        error: videoError,
        fetchUrl: fetchVideoUrl,
        reset: resetVideo,
    } = useCompletionVideoPlayback()

    const openVideoForWorkorder = (wo: WorkorderResponse) => {
        if (!wo.completion_video?.uploaded) return
        setVideoModal({ open: true, workorder: wo })
        fetchVideoUrl(wo.completion_video.id)
    }

    // Filter workorders based on search term
    const filteredWorkorders = workorderList.filter((wo) => {
        if (!searchTerm.trim()) return true

        const searchLower = searchTerm.toLowerCase()
        return (
            wo.unit_name?.toLowerCase().includes(searchLower) ||
            wo.vendor_name?.toLowerCase().includes(searchLower) ||
            wo.service_name?.toLowerCase().includes(searchLower) ||
            wo.title?.toLowerCase().includes(searchLower)
        )
    })

    const totalCount = workorderList.length
    const filteredCount = filteredWorkorders.length

    return (
        <>
            <Container direction="column" style={{ height: '100%' }}>
                {/* Header */}
                <Container
                    style={{
                        padding: materialTheme.spacing(1, 2, 1, 2),
                        borderBottom: `1px solid ${materialTheme.palette.divider}`,
                        backgroundColor: materialTheme.palette.background.paper,
                        alignItems: 'center',
                    }}
                >
                    <Container style={{ alignItems: 'center', flex: 1 }}>
                        <WalkIcon
                            style={{
                                fontSize: 28,
                                color: materialTheme.palette.primary.main,
                                marginRight: materialTheme.spacing(2),
                            }}
                        />
                        <Container direction="column">
                            <Typography
                                variant="h5"
                                style={{ fontWeight: 600 }}
                            >
                                Walklist
                            </Typography>
                        </Container>
                    </Container>

                    {/* Stats */}
                    <Container
                        style={{
                            alignItems: 'center',
                            marginRight: materialTheme.spacing(2),
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            {filteredCount === totalCount
                                ? totalCount
                                : `${filteredCount}/${totalCount}`}{' '}
                            workorders
                        </Typography>
                    </Container>

                    {/* Close Button */}
                    <IconButton
                        onClick={onClose}
                        style={{
                            backgroundColor: materialTheme.palette.grey[100],
                            marginLeft: materialTheme.spacing(1),
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Container>

                {/* Search Bar */}
                <Container
                    style={{
                        padding: materialTheme.spacing(2, 3, 1, 3),
                        borderBottom: `1px solid ${materialTheme.palette.divider}`,
                        backgroundColor: materialTheme.palette.background.paper,
                    }}
                >
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search workorders by unit, vendor, service, or title..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        style={{
                            backgroundColor:
                                materialTheme.palette.background.paper,
                        }}
                        size="small"
                    />
                </Container>

                {/* Content */}
                <Container style={{ flex: 1, overflow: 'hidden' }}>
                    <Container
                        direction="column"
                        style={{
                            flex: 1,
                            overflow: 'hidden',
                            padding: materialTheme.spacing(2, 3, 3, 3),
                        }}
                    >
                        {loading ? (
                            <Container
                                direction="column"
                                style={{
                                    flex: 1,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: materialTheme.palette.text.secondary,
                                }}
                            >
                                <CircularProgress
                                    size={60}
                                    style={{
                                        marginBottom: materialTheme.spacing(2),
                                        color:
                                            materialTheme.palette.primary.main,
                                    }}
                                />
                                <Typography variant="h6" gutterBottom>
                                    Loading walklist...
                                </Typography>
                                <Typography variant="body2">
                                    Fetching completed workorders
                                </Typography>
                            </Container>
                        ) : filteredWorkorders.length === 0 ? (
                            <Container
                                direction="column"
                                style={{
                                    flex: 1,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: materialTheme.palette.text.secondary,
                                }}
                            >
                                <SearchIcon
                                    style={{
                                        fontSize: 64,
                                        marginBottom: materialTheme.spacing(2),
                                    }}
                                />
                                <Typography variant="h6" gutterBottom>
                                    {searchTerm.trim()
                                        ? 'No matching workorders'
                                        : 'No workorders in walklist'}
                                </Typography>
                                <Typography variant="body2">
                                    {searchTerm.trim()
                                        ? `No workorders found matching "${searchTerm}"`
                                        : 'Completed workorders will appear here for inspection'}
                                </Typography>
                            </Container>
                        ) : (
                            <Container
                                direction="column"
                                style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    gap: materialTheme.spacing(2),
                                }}
                            >
                                {filteredWorkorders.map((wo) => (
                                    <WorkorderRow
                                        key={`WORKORDER-${wo.id}`}
                                        workorder={wo}
                                        user={workspaceUser}
                                        canSeeUnitNotes
                                        isSelectedWorkorder={
                                            wo.channel ===
                                            openMessageChannel.channel
                                        }
                                        openMessageDrawer={() => {
                                            const cId =
                                                openMessageChannel.channel ===
                                                wo.channel
                                                    ? -1
                                                    : wo.channel
                                            setOpenMessageChannel({
                                                channel: cId,
                                                channelType: 'workorder',
                                            })
                                        }}
                                        openUnitNotes={() => {
                                            if (!wo.unit_channel_id) {
                                                return
                                            }
                                            setOpenMessageChannel({
                                                channel: wo.unit_channel_id,
                                                channelType: 'unit_notes',
                                            })
                                        }}
                                        transitionWorkorder={(wo, status) => {
                                            return transitionWorkorder(
                                                wo,
                                                status,
                                            )
                                                .then((wo) => {
                                                    insertOrUpdateIdentifiable(
                                                        wo,
                                                        setWorkorderList,
                                                    )
                                                })
                                                .catch((e) => {
                                                    console.log(
                                                        'error updating',
                                                        e,
                                                    )
                                                })
                                        }}
                                        onClickWorkorder={() => {}}
                                        updateWorkorderState={() => {}}
                                        style={{
                                            width: '100%',
                                            borderRadius: '8px',
                                            boxShadow: materialTheme.shadows[1],
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                        onClickGoBack={() => {
                                            setOpenGoBackModal({
                                                open: true,
                                                workorder: wo,
                                            })
                                        }}
                                        onClickVideo={() => {
                                            openVideoForWorkorder(wo)
                                        }}
                                    />
                                ))}
                            </Container>
                        )}
                    </Container>

                    {openMessageChannel.channel !== -1 && (
                        <Container
                            style={{
                                width: 400,
                                borderLeft: `1px solid ${theme.palette.grey[700]}`,
                                flexDirection: 'column',
                            }}
                        >
                            <Container
                                style={{
                                    padding: theme.spacing(1),
                                    alignItems: 'center',
                                }}
                            >
                                <IconButton
                                    onClick={() => {
                                        setOpenMessageChannel({
                                            channel: -1,
                                            channelType: 'workorder',
                                        })
                                    }}
                                >
                                    <ChevronRightIcon />
                                </IconButton>

                                <span
                                    style={{
                                        ...typographyStyle(theme.typography.h5),
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                        marginLeft: theme.spacing(1),
                                    }}
                                >
                                    {openMessageChannel.channelType ===
                                    'workorder'
                                        ? 'Messages'
                                        : 'Unit Notes'}
                                </span>
                            </Container>

                            <MessageForm
                                theme={theme}
                                channelId={openMessageChannel.channel}
                                afterCreateMessage={() => {
                                    const wo = workorderList?.find(
                                        (wo) =>
                                            wo.channel ===
                                            openMessageChannel.channel,
                                    )
                                    if (wo) {
                                        insertOrUpdateIdentifiable(
                                            {
                                                ...wo,
                                                message_count:
                                                    wo.message_count + 1,
                                            },
                                            setWorkorderList,
                                        )
                                    }
                                }}
                            />
                        </Container>
                    )}
                </Container>
            </Container>

            {/* Completion Video Modal */}
            <VideoPlaybackModal
                open={videoModal.open}
                url={videoUrl}
                loading={videoLoading}
                error={videoError}
                title={
                    videoModal.workorder
                        ? `${
                              videoModal.workorder.unit_name ?? 'Workorder'
                          } — Completion Video`
                        : 'Completion Video'
                }
                onClose={() => {
                    setVideoModal({ open: false, workorder: null })
                    resetVideo()
                }}
                onRetry={() => {
                    const videoId = videoModal.workorder?.completion_video?.id
                    if (videoId !== undefined) {
                        fetchVideoUrl(videoId)
                    }
                }}
            />

            {/* Go Back Modal */}
            <GoBackModal
                open={openGoBackModal.open}
                workorder={openGoBackModal.workorder}
                onClose={() =>
                    setOpenGoBackModal({ open: false, workorder: null })
                }
                onSubmit={(workorderId, formData) => {
                    createGoBack(workorderId, formData)
                        .then((updatedWorkorder) => {
                            insertOrUpdateIdentifiable(
                                updatedWorkorder,
                                setWorkorderList,
                            )
                        })
                        .catch((error) => {
                            console.error('Error creating go back:', error)
                        })
                }}
            />
        </>
    )
}
